// ───────────────────────────────────────────────
// Shared fetch transport for BOTH the main frontend and the admin SPA.
//
// This is the single canonical request wrapper. It carries:
//   • the httpOnly session cookie on every call (`credentials: "include"`),
//   • a centralized 401 handler (registered by the app's AuthProvider) that
//     logs the user out / redirects to login,
//   • FastAPI error coercion: a string `detail` (HTTPException) or a 422
//     validation-error list are both flattened to a readable message so the UI
//     never renders "[object Object]".
//
// Network failures (offline, DNS, connection reset) reject WITHOUT touching the
// 401 handler — a flaky connection must never force a logout.
//
// App-specific concerns stay out of here:
//   • the endpoint maps (`api` objects) live in each app's lib/api.js,
//   • per-request extra headers (e.g. the frontend's X-Active-Archer) are
//     supplied via setExtraHeadersProvider so the admin doesn't inherit them.
// ───────────────────────────────────────────────

let unauthorizedHandler = null;

// Registered by AuthProvider. Called whenever the API answers 401.
export function setUnauthorizedHandler(fn) {
  unauthorizedHandler = fn;
}

// Optional hook returning a plain object of extra headers to merge into every
// request (the frontend uses it for X-Active-Archer). Returning a falsy value
// or null adds nothing — the admin never registers one.
let extraHeadersProvider = null;

export function setExtraHeadersProvider(fn) {
  extraHeadersProvider = fn;
}

// Error bodies come in three shapes:
//   • the structured convention (#2052) — `{code, message, ...extra}`, which
//     every non-validation error now uses,
//   • a request-validation (422) list of `{loc, msg, type}` objects, which
//     FastAPI owns and which keeps its native shape so the UI can still render
//     per-field messages,
//   • a bare string, from any older/third-party surface.
// Coerce all three into a readable string so the UI never shows
// "[object Object]".
//
// Legacy string for v3.17.0 bundles — remove after the release following
// v3.18.0. For exactly ONE code the backend still answers with the pre-#2052
// bare string, `{"detail": "stale_end"}`: tabs left open across that deploy
// run the v3.17.0 bundle, whose only test for it is
// `err.body.detail === "stale_end"`. This map is the explicit allow-list of
// such strings (code → the prose its object envelope carried). It must stay
// this narrow: a string detail is otherwise human-readable text ("Round not
// found"), and treating arbitrary prose as a machine code is exactly what
// #2052 removed. A Map, not an object, so "toString"/"constructor" never match.
const LEGACY_STRING_CODES = new Map([
  ["stale_end", "Another device has already advanced this round."],
]);

export function formatErrorDetail(detail) {
  if (detail == null) return null;
  // An allow-listed legacy code is a machine string — show its prose, never
  // the raw identifier.
  if (typeof detail === "string") return LEGACY_STRING_CODES.get(detail) ?? detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => {
        if (typeof e === "string") return e;
        const field = Array.isArray(e?.loc) ? e.loc.join(".") : null;
        return field && e?.msg ? `${field}: ${e.msg}` : e?.msg || JSON.stringify(e);
      })
      .join("; ");
  }
  // Structured error: show the human prose, never the machine payload.
  if (typeof detail.message === "string" && detail.message) return detail.message;
  return JSON.stringify(detail);
}

// Pull the machine-readable code out of an error body's `detail` (#2052).
function errorCodeOf(detail) {
  // Legacy string detail — allow-listed codes ONLY (see LEGACY_STRING_CODES).
  if (typeof detail === "string") return LEGACY_STRING_CODES.has(detail) ? detail : null;
  if (detail && typeof detail === "object" && !Array.isArray(detail)) {
    if (typeof detail.code === "string" && detail.code) return detail.code;
  }
  return null;
}

// THE one reader of the backend's machine-readable error code (#2052).
//
// Branch on this — NEVER on `err.message`, which is free prose the backend may
// reword or translate at any time without it being a breaking change. Returns
// null when the response carries no code (a validation 422, a non-JSON error,
// a network fault), so `apiErrorCode(err) === "..."` is always safe to write.
//
// `request` below pre-parses the code onto `err.code`; this reads through to
// the raw body as a fallback so a hand-constructed error object (a test double,
// a future non-transport caller) resolves identically rather than silently
// failing every branch.
export function apiErrorCode(err) {
  if (!err) return null;
  if (typeof err.code === "string" && err.code) return err.code;
  return errorCodeOf(err.body?.detail);
}

export async function request(path, { method = "GET", body, signal, headers: extra, cache } = {}) {
  // FormData bodies are sent as-is so the browser sets the multipart
  // boundary; JSON bodies are stringified with an explicit content type.
  const isForm = body instanceof FormData;
  const headers = {};
  if (body && !isForm) headers["Content-Type"] = "application/json";
  const provided = extraHeadersProvider?.();
  if (provided) Object.assign(headers, provided);
  if (extra) Object.assign(headers, extra);

  // `fetch` rejecting here (offline, DNS, connection reset) propagates as a
  // network error — it never reaches the 401 path below, so it cannot log
  // the user out.
  const res = await fetch(path, {
    method,
    signal,
    credentials: "include",
    // `cache` is opt-in (e.g. "no-store" to bypass the browser HTTP cache after
    // a mutation invalidates server reference data). Omitted → fetch default.
    ...(cache ? { cache } : null),
    headers: Object.keys(headers).length ? headers : undefined,
    body: isForm ? body : body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    if (res.status === 401) unauthorizedHandler?.();
    let msg = `${res.status} ${res.statusText}`;
    let data = null;
    try {
      data = await res.json();
      const detail = formatErrorDetail(data?.detail);
      if (detail) msg = `${res.status} ${detail}`;
    } catch {
      /* swallow */
    }
    const apiErr = new Error(msg);
    apiErr.status = res.status;
    apiErr.body = data;
    // Machine-readable error code (#2052), parsed here exactly once so no
    // component has to reach into `err.body.detail` itself. null when the
    // response carries no code.
    apiErr.code = errorCodeOf(data?.detail);
    throw apiErr;
  }
  if (res.status === 204) return null;
  return res.json();
}
